import React, { useMemo, useState } from 'react';
import Layout, { EmptyState, Pager, Spinner } from '../components/Layout';
import Modal from '../components/Modal';
import { CardBadges, EditTypeChip, ReceptionStamp } from '../components/StatusBadge';
import CaseNotes from '../components/CaseNotes';
import { PrescriptionStrip } from '../components/Prescription';
import StudyPanel from '../components/StudyPanel';
import PatientLink from '../components/PatientLink';
import ShareLinks from '../components/ShareLinks';
import { useCaseAction, useCases } from '../hooks/useCases';
import { cases as casesApi } from '../utils/api';
import { formatDateTime, formatNumber } from '../utils/format';

const TABS = [
  { id: 'queue', label: 'در انتظار اسکن', status: 'reception_completed' },
  { id: 'done', label: 'اسکن‌های من', status: '' },
];

export default function ImagingDashboard() {
  const [tab, setTab] = useState('queue');
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState(null);

  const params = useMemo(() => {
    // «اسکن‌های من» را سرور فیلتر می‌کند، نه مرورگر: با صفحه‌بندی، فیلتر
    // سمت کلاینت فقط روی همان پنجاه‌تای صفحه‌ی جاری کار می‌کرد.
    const base =
      tab === 'queue' ? { status: 'reception_completed' } : { scanned_by_me: 'true' };
    return { ...base, ...(search ? { q: search } : {}) };
  }, [tab, search]);

  const { cases, count, page, setPage, pageCount, pageSize, loading, error, reload } =
    useCases(params);
  const { run, pendingId } = useCaseAction(reload);

  return (
    <Layout
      title="میز تصویربرداری"
      subtitle="پس از بارگذاری فایل اسکن در فضای ابری، آن را اینجا تأیید کنید"
      tabs={TABS.map((t) => ({ ...t, count: t.id === tab ? count : undefined }))}
      activeTab={tab}
      onTabChange={setTab}
    >
      <div className="toolbar">
        <input
          type="search"
          className="input input--search"
          placeholder="جست‌وجوی نام بیمار یا شماره نسخه…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {loading && <Spinner />}
      {error && <p className="alert alert--danger">{error}</p>}

      {!loading && !error && cases.length === 0 && (
        <EmptyState
          icon="🩻"
          title={tab === 'queue' ? 'بیماری در انتظار اسکن نیست.' : 'هنوز اسکنی تأیید نکرده‌اید.'}
          hint={tab === 'queue' ? 'با ثبت بیمار جدید توسط پذیرش، اینجا به‌روز می‌شود.' : undefined}
        />
      )}

      <div className="cards">
        {cases.map((item) => (
          <article key={item.id} className={`card ${item.is_urgent ? 'card--urgent' : ''}`}>
            <header className="card__head">
              <div>
                <h3 className="card__title"><bdi>{item.name}</bdi></h3>
                <p className="card__meta">
                  <bdi>{item.doctor}</bdi> · نسخه <span className="num">{item.order_number}</span>
                </p>
              </div>
              <CardBadges item={item} />
            </header>

            <div className="card__row">
              <EditTypeChip code={item.edit_type_code} name={item.edit_type_name} />
              <ReceptionStamp at={item.reception_time} />
            </div>

            <PrescriptionStrip items={item.prescriptions} />

            {/* لینک و QR — تصویربردار همین‌جا کپی می‌کند و روی کلیشه می‌گذارد */}
            <PatientLink item={item} editable onChanged={reload} />

            {/* لینک خودِ فلوترک — تصویر و گزارش را داخل سامانه نشان می‌دهد،
                برخلاف لینک بالا که به فضای ابری پرتو می‌رود. */}
            <ShareLinks caseId={item.id} />

            <CaseNotes caseId={item.id} notes={item.notes} onChanged={reload} />

            {/* تصاویر مستقیم روی سرور خودمان. مسیر اصلی، عاملِ خودکار روی
                کامپیوتر تصویربرداری است؛ این پنجره جایگزین دستی آن. */}
            <StudyPanel
              caseId={item.id}
              kind="raw"
              canUpload
              title="تصاویر دایکام"
              hint="فولدر خروجی دستگاه را رها کنید؛ زیپ کردنش با مرورگر است."
            />

            {item.scan_confirmed ? (
              <dl className="card__facts">
                <div>
                  <dt>تأیید اسکن</dt>
                  <dd>{formatDateTime(item.scan_time)}</dd>
                </div>
                <div>
                  <dt>کارشناس</dt>
                  <dd><bdi>{item.radiographer_name}</bdi></dd>
                </div>
              </dl>
            ) : (
              <footer className="card__foot">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={pendingId === item.id}
                  onClick={() => setConfirming(item)}
                >
                  ☁️ تأیید بارگذاری اسکن
                </button>
              </footer>
            )}
          </article>
        ))}
      </div>

      {!loading && cases.length > 0 && pageCount === 1 && (
        <p className="list-footnote">{formatNumber(count)} پرونده در این فهرست</p>
      )}

      <Pager
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        count={count}
        onChange={setPage}
      />

      <ConfirmScanModal
        item={confirming}
        pending={pendingId === confirming?.id}
        onClose={() => setConfirming(null)}
        onConfirm={async (url) => {
          const result = await run(
            confirming.id,
            () => casesApi.confirmScan(confirming.id, url ? { scan_cloud_url: url } : {}),
            'اسکن تأیید شد و ادیتورها مطلع شدند.'
          );
          if (result) setConfirming(null);
        }}
      />
    </Layout>
  );
}

function ConfirmScanModal({ item, pending, onClose, onConfirm }) {
  const [url, setUrl] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  React.useEffect(() => {
    setUrl('');
    setAcknowledged(false);
  }, [item?.id]);

  if (!item) return null;

  return (
    <Modal
      open
      title="تأیید بارگذاری اسکن"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            انصراف
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!acknowledged || pending}
            onClick={() => onConfirm(url.trim())}
          >
            {pending ? 'در حال ثبت…' : 'تأیید و ارسال به صف'}
          </button>
        </>
      }
    >
      <p className="modal__lead">
        بیمار <strong><bdi>{item.name}</bdi></strong> — نسخه <span className="num">{item.order_number}</span>
      </p>

      <div className="field">
        <label htmlFor="scan-url">نشانی فایل در فضای ابری (اختیاری)</label>
        <input
          id="scan-url"
          type="url"
          dir="ltr"
          placeholder="https://…"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
        />
        <span className="field__hint">
          فایل در فضای ابری می‌ماند؛ سامانه فقط نشانی و زمان را نگه می‌دارد.
        </span>
      </div>

      <label className={`check ${acknowledged ? 'is-checked' : ''}`}>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        />
        <span>فایل اسکن را در فضای ابری بارگذاری کرده‌ام.</span>
      </label>

      <p className="form__note">ساعت تأیید و نام شما به‌صورت خودکار ثبت می‌شود.</p>
    </Modal>
  );
}
